import { useEffect, useState } from 'react';
import PortaledModal from '../../../../components/Modals/PortaledModal';
import { useShippingPageContext } from '../providers/ShippingPageProvider';
import { useCartContext } from '../../../../customHooks/useCart';
import { PRODUCT_TYPE_FREE_DRUG, PRODUCT_TYPE_PRESCRIPTION_DRUG } from '../constants';
import { FLOWZZ_PREMIUM } from '../../../../utils/consts';
import * as translator from '../../../../utils/translator';
import { getPrice } from '../../../../utils/translator';
import { formatCannabisDisplayData } from '../../../ChooseTreatmentVariants/helpersV2';

import XIcon from '../../../../icons/X.svg';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';


const OutOfStockModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onChooseAnother: () => void;
  language: string;
}> = ({ isOpen, onClose, onChooseAnother, language }) => {

  const { outOfStockDataState } = useShippingPageContext();
  const { getCart } = useCartContext();
  const cart = getCart();
  const locale = cart?.locale_id || 'de-DE';

  const [animationState, setAnimationState] = useState<string>("");

  // Split changed and removed
  const changed = outOfStockDataState.outOfStockData.filter((item) => item.newOTCQuantity > 0 || item.newProductItemId !== 0);
  const removed = outOfStockDataState.outOfStockData.filter((item) => item.newOTCQuantity === 0 && item.newProductItemId === 0);

  const isOTCOnly = outOfStockDataState.outOfStockData.every((item) => item.productType === PRODUCT_TYPE_FREE_DRUG);
  const isRXOnly = outOfStockDataState.outOfStockData.every((item) => item.productType === PRODUCT_TYPE_PRESCRIPTION_DRUG);
  const isAllProductsOOS = cart?.came_from === FLOWZZ_PREMIUM && removed.length > 0 && removed.length === outOfStockDataState.originalProducts.length;

  // Handle animation state
  useEffect(() => {
    if (!animationState){
      setAnimationState("hidden");
    }
    else if (isOpen) {
      document.body.style.overflow = "hidden"; // Prevent page scrolling when modal is open

      setAnimationState("animate-fade-in-up");
      if (changed.length > 0 && removed.length > 0) {
        MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': 'Out of stock and changed quantity'})
      }
      else if (changed.length > 0) {
        MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': 'Changed quantity products'})
      }
      else if (removed.length > 0) {
        MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': 'Out of stock products'})
      }
    }
    else {
      setAnimationState("animate-fade-out-down");
      document.body.style.overflow = "auto"; // Allow page scrolling when modal is closed
    }
    return () => {
      document.body.style.overflow = "auto"; // Allow page scrolling when modal is closed
    }
  }, [isOpen]);

  // Don't render at all if it's hidden
  if (animationState === "hidden" && !isOpen) return null;

  // Find product details by product_item_id
  const getOldProductDetails = (productItemId: number, isOTC: boolean) => {
    const productDetails = outOfStockDataState.originalProducts.find((p: CartProductView) => p.product_item_id === productItemId); 
    if (isOTC) {
      if (!productDetails) return null;
      return {
        ...productDetails,
        quantityStr: productDetails.quantity
      }
    }
    return productDetails;
  };

  const getNewProductDetails = (productItemId: number, isOTC: boolean) => {
    const productDetails = cart.products.find((p: CartProductView) => p.product_item_id === productItemId);
    if (!productDetails) return null;
    if (isOTC) {
      return {
        ...productDetails,
        quantityStr: productDetails.quantity
      }
    }
    return productDetails;
  };

  const parseAdditionalData = (additionalData: any) => {
    const additionalDataObj = typeof additionalData === 'string' ? JSON.parse(additionalData) : additionalData || {};
    
    const { thcDisplay, cbdDisplay, strain } = formatCannabisDisplayData(additionalDataObj);
    const customDosageForCannabis = (thcDisplay && cbdDisplay) ? `THC: ${thcDisplay} | CBD: ${cbdDisplay}` : '';

    return { customDosageForCannabis, strain }
  }

  const handleClose = () => {
    setAnimationState("animate-fade-out-down");
    setTimeout(() => {
      setAnimationState("");
      onClose();
    }, 500);
  }

  const buildMixpanelEventData = () => {
    const eventData: { eventName: string; type: string; properties: Record<string, any> } = {
      eventName: "",
      type: "",
      properties: {},
    };

    // Set event name
    if (changed.length > 0 && removed.length > 0) {
      eventData.eventName = "Decision on Out of Stock and Changed Quantity Popup";
    } else if (removed.length > 0) {
      eventData.eventName = "Decision on Out of Stock Products Popup";
    } else if (changed.length > 0) {
      eventData.eventName = "Decision on Changed Quantity Products Popup";
    }

    // Set event type
    if (isOTCOnly) {
      eventData.type = "OTC Only";
    } else if (isRXOnly) {
      eventData.type = "RX Only";
    } else {
      eventData.type = "OTC and RX";
    }

    return eventData;
  };

  const eventData = buildMixpanelEventData();

  const sendMixpanelEvent = (decision: string) => {

    const outOfStockProducts = removed.map((item) => {
        const isOTC = item.productType === PRODUCT_TYPE_FREE_DRUG;
        const product = getOldProductDetails(item.outOfStockProductItemId, isOTC);
        return product ? product.title : '';
      }).filter(title => title).join(', ');

    const eventProperties = { 
      ...eventData.properties, 
      'Pop-up Type': eventData.type, 
      'Decision': decision,
      ...(outOfStockProducts.length > 0 && { 'Out of Stock Products': outOfStockProducts })
    };

    MainTracker.track('pv', eventData.eventName, eventProperties);

  }


  return (
    <PortaledModal 
      shouldShowModal={(animationState !== "")} 
      hideModal={() => {
        handleClose()
        sendMixpanelEvent('Close')
      }} 
      shouldBackgroundClickHideModal={true}
    >
      <div 
        id="out-of-stock-modal" 
        className={`w-full max-w-[430px] md:max-w-[460px] max-h-[80vh] bg-white rounded-[30px_30px_0px_0px] md:rounded-[8px_8px_8px_8px] shadow-2xl pt-0 md:pl-4 md:pr-4 pb-4 flex flex-col overflow-hidden fixed bottom-0 left-0 right-0 md:relative md:mx-auto md:my-16 ${animationState ? animationState : "hidden"}`}
      >
        {/* Mobile top scroller */}
        <div 
          className="w-full flex justify-center pt-3 pb-2 md:hidden cursor-pointer"
          onTouchStart={(e) => {
            const startY = e.touches[0].clientY;
            e.currentTarget.dataset.startY = startY.toString();
          }}
          onTouchMove={(e) => {
            const startY = parseFloat(e.currentTarget.dataset.startY || '0');
            const currentY = e.touches[0].clientY;
            const deltaY = currentY - startY;
            
            if (deltaY > 50) { // Threshold for swipe down
              handleClose()
              sendMixpanelEvent('Close')
            }
          }}
        >
          <div className="w-24 h-1.5 bg-[#EDEDED] rounded-full" />
        </div>

        {/* Scrollable content */}
        <div id="out-of-stock-modal-content" className="flex-1 overflow-y-auto custom-scrollbar">
            {/* Header */}
        <div 
          id="out-of-stock-modal-header" 
          className="relative flex flex-col items-center justify-center pb-2 px-6"
        >
          <div className="w-full flex flex-row items-center justify-center">
            <div 
              id="out-of-stock-modal-title" 
              className="bg-[#0A9281] max-w-[300px] text-center text-white font-extrabold text-sm px-4 py-2 rounded-b-3xl tracking-wide"
            >
              {translator.translate(language, "shipping", "OutOfStockModal/header")}
            </div>
            <button 
              id="out-of-stock-modal-close-btn" 
              className="absolute right-2 top-4 w-6 h-6 p-0 bg-transparent border-none cursor-pointer hidden md:block" 
              onClick={() => {
                handleClose()
                sendMixpanelEvent('Close')
              }}
            >
              <img src={XIcon} alt="Close" className="w-6 h-6" />
            </button>
          </div>
          {false && (
            <div className="w-12 h-1.5 bg-[#C7E6DF] rounded-full mt-3 mb-1" />
          )}
        </div>
          {/* Info message */}
          <div id="out-of-stock-modal-info" className="px-4 md:px-6 pt-2 pb-2 text-left">
            <p className="text-[#0A9281] font-poppins font-semibold text-base mb-1">
                {translator.translate(language, "shipping", "OutOfStockModal/title")}
            </p>
          </div>

          {/* Changed quantity section */}
          {changed.length > 0 && (
            <>
            <p id="out-of-stock-modal-changed-title" className="px-4 md:px-6 text-[#3B3B3B] font-poppins font-normal text-sm">
              {translator.translate(language, "shipping", "OutOfStockModal/section_1_title")}
            </p>
            <div id="out-of-stock-modal-changed-section" className="bg-[#F3F9F8] rounded-xl mx-4 md:mx-6 mt-4 mb-4 p-4">
              <div id="out-of-stock-modal-changed-subtitle" className="text-[#000000] font-medium text-base mb-3 text-center">
                <span className="text-[#0A9281] italic mr-2">{translator.translate(language, "shipping", "OutOfStockModal/section_1_subtitle_bold")}</span>{translator.translate(language, "shipping", "OutOfStockModal/section_1_subtitle")}
              </div>
              <div id="out-of-stock-modal-changed-products" className="flex flex-col gap-3">
                {changed.map((item, index) => {

                  const isOTC = item.productType === PRODUCT_TYPE_FREE_DRUG
                  const oldProduct = getOldProductDetails(item.outOfStockProductItemId, isOTC);
                  const newProduct = getNewProductDetails(isOTC ? item.outOfStockProductItemId : item.newProductItemId, isOTC);

                  if (!oldProduct || !newProduct) return null;

                  const { customDosageForCannabis, strain } = parseAdditionalData(oldProduct.additionalData);
                  const [oldQuantity, oldQuantityType] = oldProduct.quantityStr.toString().split(' ');
                  const [newQuantity, newQuantityType] = newProduct.quantityStr.toString().split(' ');

                  const eventProperties = {
                    [`Changed Quantity Product Name ${index + 1}`]: oldProduct.title,
                    [`Old Quantity ${index + 1}`]: parseFloat(oldQuantity),
                    [`New Quantity ${index + 1}`]: parseFloat(newQuantity)
                  }

                  eventData.properties = { ...eventData.properties, ...eventProperties };

                  return (
                    <div id={`out-of-stock-changed-product-${item.outOfStockProductItemId}`} className="flex items-start gap-4 bg-white rounded-lg p-3" key={item.outOfStockProductItemId}>
                      <img className="w-14 h-14 rounded-md object-cover border border-[#E5E7EB] flex-shrink-0" src={oldProduct.imageSrc} alt={oldProduct.title} />
                      <div className="flex-1 flex flex-col gap-1">
                        <div className="font-medium text-[#363636] text-xs">{oldProduct.title}</div>
                        {(customDosageForCannabis || strain) && (
                          <div className="flex flex-row text-[10px] text-[#747474] gap-2">
                            <span>{customDosageForCannabis}</span>
                            <span>{strain}</span>
                          </div>
                        )}
                        <div className="flex items-center mt-2">
                          <div id={`out-of-stock-old-quantity-${item.outOfStockProductItemId}`} className="flex flex-col items-center w-20 border border-solid border-[#E5E5E5] rounded-md p-1">
                            <div className="flex flex-row gap-1 text-[#B0B7C3]">
                              <span className="text-[15px]">{oldQuantity}</span>
                              <span className="text-[10px] mt-1">{oldQuantityType}</span>
                            </div>
                            <span className="text-[10px] text-[#B0B7C3]">
                              {getPrice(oldProduct.official_price * oldProduct.quantity, locale)}
                            </span>
                          </div>
                          <div className="w-4 h-4 bg-[#0A9281] rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white h-[34px] text-lg font-bold">→</span>
                          </div>
                          <div id={`out-of-stock-new-quantity-${item.outOfStockProductItemId}`} className="flex flex-col items-center w-20 border border-solid border-[#E5E5E5] rounded-md p-1">
                            <div className="flex flex-row gap-1 text-[#0A9281]">
                              <span className="text-[15px]">{newQuantity}</span>
                              <span className="text-[10px] mt-1">{newQuantityType}</span>
                            </div>
                            <span className="text-[10px] text-[#000000] font-medium">
                              {getPrice(newProduct.official_price * newProduct.quantity, locale)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            </>
          )}

          {/* Removed products section */}
          {removed.length > 0 && (
            <>
              <p id="out-of-stock-modal-removed-title" className="px-4 md:px-6 text-[#3B3B3B] font-poppins font-normal text-sm">
                {translator.translate(language, "shipping", "OutOfStockModal/section_2_title")}
              </p>
            <div id="out-of-stock-modal-removed-section" className="bg-[#F3F9F8] rounded-xl mx-4 md:mx-6 mt-4 mb-4 p-4">
              <div id="out-of-stock-modal-removed-subtitle" className="text-[#000000] font-medium text-base mb-3 text-center">
                <span className="text-[#0A9281] italic mr-2">{translator.translate(language, "shipping", "OutOfStockModal/section_2_subtitle_bold")}</span>{translator.translate(language, "shipping", "OutOfStockModal/section_2_subtitle")}
              </div>
              <div id="out-of-stock-modal-removed-products" className="flex flex-col gap-3">
                {removed.map((item) => {
                  const isOTC = item.productType === PRODUCT_TYPE_FREE_DRUG
                  const product = getOldProductDetails(item.outOfStockProductItemId, isOTC);
                  if (!product) return null;
                  const { customDosageForCannabis, strain } = parseAdditionalData(product.additionalData);

                  return (
                    <div id={`out-of-stock-removed-product-${item.outOfStockProductItemId}`} className="flex items-center gap-4 bg-white rounded-lg p-3" key={item.outOfStockProductItemId}>
                      <img className="w-14 h-14 rounded-md object-cover border border-[#E5E7EB]" src={product.imageSrc} alt={product.title} />
                      <div className="flex-1 flex flex-col gap-1">
                        <div className="font-medium text-[#363636] text-xs">{product.title}</div>
                        {(customDosageForCannabis || strain) && (
                          <div className="flex flex-row text-[10px] text-[#747474] gap-2">
                            <span>{customDosageForCannabis}</span>
                            <span>{strain}</span>
                          </div>
                        )}
                        <div className="text-[10px] text-[#0D2C54] mt-1 text-right">{getPrice(product.official_price * product.quantity, locale)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            </>
          )}

          {/* Footer message */}
          <div id="out-of-stock-modal-footer" className="px-4 md:px-6 pt-2 pb-4 text-[#3B3B3B] text-sm">
            {translator.translate(language, "shipping", isAllProductsOOS ? "OutOfStockModal/footer_all_removed" : "OutOfStockModal/footer")}
          </div>
        </div>

        {/* Action buttons */}
        <div id="out-of-stock-modal-actions" className="flex flex-col gap-3 px-4 md:px-6 pb-8 pt-4 md:pb-4">
          <button
            id="out-of-stock-modal-proceed-btn"
            disabled={isAllProductsOOS}
            className="w-full py-3 rounded-lg bg-[#0A9281] border-none text-white font-bold text-base shadow hover:bg-[#0A9281]/90 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
                handleClose()
                sendMixpanelEvent('Ok, continue')
            }}
          >
            {translator.translate(language, "shipping", "OutOfStockModal/button_1")}
          </button>

          {!isOTCOnly && (
            <button
                id="out-of-stock-modal-choose-another-btn"
                className="w-full py-3 rounded-lg bg-white border-[1.5px] border-black/30 text-[#0D2C54] font-bold text-base hover:bg-[#F7F7F7] cursor-pointer"
                onClick={() => {
                  sendMixpanelEvent('To choose treatment')
                  onChooseAnother()
                }}
            >
                {translator.translate(language, "shipping", "OutOfStockModal/button_2")}
            </button>
          )}
        </div>
      </div>
    </PortaledModal>
  );
};

export default OutOfStockModal;
